import { Star } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCompactNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { SearchedRepo } from "@/lib/types";

interface RepoListProps {
  repos: SearchedRepo[];
  highlightedRepo: string | null;
  onHoverRepo: (repo: string | null) => void;
}

export function RepoList({ repos, highlightedRepo, onHoverRepo }: RepoListProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Repositories</CardTitle>
        <p className="text-sm text-muted-foreground">{repos.length}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {repos.map(repo => {
          const repoName = `${repo.owner}/${repo.name}`;
          const active = highlightedRepo === repoName;

          return (
            <a
              key={repoName}
              className={cn(
                "draft-box block bg-background/50 p-4 transition-colors hover:bg-muted/20",
                active ? "bg-primary/5 text-foreground" : "bg-background/50",
              )}
              href={`https://github.com/${repoName}`}
              target="_blank"
              rel="noreferrer"
              onMouseEnter={() => onHoverRepo(repoName)}
              onMouseLeave={() => onHoverRepo(null)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-medium text-foreground">{repoName}</h3>
                  {repo.description ? <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{repo.description}</p> : null}
                </div>
                <Badge variant="outline" className="gap-1">
                  <Star className="size-3.5" />
                  {formatCompactNumber(repo.stars)}
                </Badge>
              </div>
            </a>
          );
        })}
      </CardContent>
    </Card>
  );
}
